import { useCallback, useEffect, useMemo, useState } from 'react';
import { LandPlot, Printer, Download, X, Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react';

import { requestClosePrintPreview } from './presentSystemPrint';
import {
  REVISION_PRINT_A4_HEIGHT_PX,
  REVISION_PRINT_A4_WIDTH_PX,
  buildRevisionPrintHtml,
  chunkRevisionPrintParticles,
  filterPrintableRevisionParticles,
  resolveRevisionPrintPreviewScale,
  shortRevisionId,
  type PrintSheetKind,
  type RevisionPrintParticle,
} from './RevisionPrintAction';

function resolveSubCardCount(particle: RevisionPrintParticle) {
  return (particle.subCards && particle.subCards.length > 0
    ? particle.subCards
    : (particle.subQuestions || []).map((question) => ({ question, answer: '' })))
    .filter((item) => item.question.trim() || String(item.answer || '').trim()).length;
}

function buildParticlePrintChipLabel(particle: RevisionPrintParticle, index: number) {
  const expanded = particle.subQuestionsExpanded !== false;
  const subCount = resolveSubCardCount(particle);
  const particleLabel = `${index + 1}. ${shortRevisionId(particle.id)}`;
  if (subCount <= 0) {
    return `${particleLabel} · 0 子题`;
  }
  return `${particleLabel} · ${subCount} 子题${expanded ? '' : '（已收起）'}`;
}

/**
 * Continuous A4 WYSIWYG preview: one document = print HTML (no nested scroll pages).
 * Scale is applied on the iframe shell so layout matches system print 1:1.
 */
function RevisionPrintContinuousPreview({
  particles,
  sessionId,
  printSessionId,
  sheetKind = 'pas',
  ttqaId = null,
  viewportWidth,
}: {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  printSessionId?: string | null;
  sheetKind?: PrintSheetKind;
  ttqaId?: string | null;
  viewportWidth: number;
}) {
  const [docLoading, setDocLoading] = useState(true);
  const pageCount = Math.max(1, chunkRevisionPrintParticles(particles).length);
  const scale = resolveRevisionPrintPreviewScale(viewportWidth);
  const shellWidth = Math.round(REVISION_PRINT_A4_WIDTH_PX * scale);
  // Force HTML builder previewScale=1 so each .print-page is true A4 px (所见即所得).
  // Outer iframe is then CSS-scaled to fit the modal width.
  const pageGapPx = 16;
  const bodyPadY = 16 + 24;
  const documentHeight = bodyPadY
    + pageCount * REVISION_PRINT_A4_HEIGHT_PX
    + Math.max(0, pageCount - 1) * pageGapPx;
  const shellHeight = Math.round(documentHeight * scale);

  const documentHtml = useMemo(
    () => buildRevisionPrintHtml({
      particles,
      sessionId,
      printSessionId: printSessionId || '',
      sheetKind,
      ttqaId,
      // Internal layout at 100% A4; visual scale happens on the iframe shell.
      previewViewportWidth: REVISION_PRINT_A4_WIDTH_PX + 32,
    }),
    [particles, printSessionId, sessionId, sheetKind, ttqaId],
  );

  useEffect(() => {
    setDocLoading(true);
  }, [documentHtml]);

  return (
    <div
      className="relative mx-auto shrink-0 overflow-hidden rounded-lg bg-white shadow-[0_8px_28px_rgba(15,23,42,0.12)]"
      style={{
        width: shellWidth,
        height: shellHeight,
        minWidth: shellWidth,
        minHeight: shellHeight,
        flexShrink: 0,
      }}
    >
      <iframe
        title="print-preview-continuous"
        srcDoc={documentHtml}
        scrolling="no"
        className="border-0 bg-white"
        style={{
          width: REVISION_PRINT_A4_WIDTH_PX,
          height: documentHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          overflow: 'hidden',
          display: 'block',
        }}
        onLoad={() => setDocLoading(false)}
        onError={() => setDocLoading(false)}
      />
      {docLoading ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/55">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      ) : null}
    </div>
  );
}

export type RevisionPrintPreviewModalProps = {
  visible: boolean;
  particles: RevisionPrintParticle[];
  resolvingEmbeddedImages?: boolean;
  sessionId?: string | null;
  printSessionId?: string | null;
  sheetKind?: PrintSheetKind;
  ttqaId?: string | null;
  previewTitle?: string;
  printing?: boolean;
  sharing?: boolean;
  subQuestionsSavingByParticleId?: Record<string, boolean>;
  onClose: () => void;
  onPrint: () => void;
  onShare: () => void;
  onToggleSubQuestionsExpanded?: (particleId: string, expanded: boolean) => void;
  onBulkToggleSubQuestionsExpanded?: (expanded: boolean) => void;
};

export function RevisionPrintPreviewModal({
  visible,
  particles,
  resolvingEmbeddedImages = false,
  sessionId,
  printSessionId,
  sheetKind = 'pas',
  ttqaId = null,
  previewTitle,
  printing = false,
  sharing = false,
  subQuestionsSavingByParticleId = {},
  onClose,
  onPrint,
  onShare,
  onToggleSubQuestionsExpanded,
  onBulkToggleSubQuestionsExpanded,
}: RevisionPrintPreviewModalProps) {
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? Math.min(window.innerWidth, 960) : 800
  ));

  useEffect(() => {
    if (!visible) return undefined;
    const sync = () => setViewportWidth(Math.min(window.innerWidth, 960));
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [visible]);

  const printableParticles = useMemo(
    () => filterPrintableRevisionParticles(particles),
    [particles],
  );
  const pages = useMemo(() => chunkRevisionPrintParticles(printableParticles), [printableParticles]);
  const busy = printing || sharing;
  const showParticleControls = Boolean(onToggleSubQuestionsExpanded && particles.length > 0);
  const particlesWithSubQuestions = useMemo(
    () => particles.filter((particle) => resolveSubCardCount(particle) > 0),
    [particles],
  );
  const showBulkSubQuestionControls = Boolean(
    onBulkToggleSubQuestionsExpanded && particlesWithSubQuestions.length > 0,
  );
  const anySubQuestionsSaving = useMemo(
    () => particlesWithSubQuestions.some((particle) => Boolean(subQuestionsSavingByParticleId[particle.id])),
    [particlesWithSubQuestions, subQuestionsSavingByParticleId],
  );
  const hasPrintableParticles = printableParticles.length > 0;
  const actionsLocked = busy || !hasPrintableParticles;

  const handleRequestClose = useCallback(() => {
    requestClosePrintPreview({
      busy,
      onClose,
      title: '正在执行打印任务',
      message: '是否退出？当前任务可能较慢或卡住，退出后可重新打开打印预览。',
      stayLabel: '继续等待',
      exitLabel: '退出',
    });
  }, [busy, onClose]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleRequestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleRequestClose, visible]);

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#f8fafc]">
      <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <LandPlot className="h-[18px] w-[18px] text-blue-700" strokeWidth={2.2} />
            <h2 className="truncate text-base font-extrabold text-slate-900">
              {previewTitle || '打印实战答题纸'}
            </h2>
          </div>
          <p className="mt-0.5 text-xs font-semibold text-slate-500">
            {`已选 ${printableParticles.length}/${particles.length} 个知识颗粒 · ${pages.length} 页 · 与打印输出同源 HTML`}
            {resolvingEmbeddedImages ? ' · 嵌入图后台准备中' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRequestClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
          aria-label="关闭"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-5 py-3">
        <button
          type="button"
          onClick={onPrint}
          disabled={actionsLocked}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-700 px-3 text-sm font-extrabold text-white disabled:opacity-50"
        >
          <Printer className="h-[18px] w-[18px]" />
          {printing ? '正在调用打印' : '系统打印'}
        </button>
        <button
          type="button"
          onClick={onShare}
          disabled={actionsLocked}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm font-extrabold text-blue-700 disabled:opacity-50"
        >
          <Download className="h-[18px] w-[18px]" />
          {sharing ? '正在生成' : '生成 PDF'}
        </button>
      </div>

      {showParticleControls ? (
        <div className="border-b border-slate-200 bg-white">
          {showBulkSubQuestionControls ? (
            <div className="flex items-center gap-2 px-5 pt-2">
              <button
                type="button"
                onClick={() => onBulkToggleSubQuestionsExpanded?.(true)}
                disabled={busy || anySubQuestionsSaving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-bold text-blue-700 disabled:opacity-50"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                全部展开子题
              </button>
              <button
                type="button"
                onClick={() => onBulkToggleSubQuestionsExpanded?.(false)}
                disabled={busy || anySubQuestionsSaving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-500 disabled:opacity-50"
              >
                <Minimize2 className="h-3.5 w-3.5" />
                全部收起子题
              </button>
            </div>
          ) : null}
          <div className="flex gap-2 overflow-x-auto px-5 py-2">
            {particles.map((particle, index) => {
              const expanded = particle.subQuestionsExpanded !== false;
              const saving = Boolean(subQuestionsSavingByParticleId[particle.id]);
              const subCount = resolveSubCardCount(particle);
              const canToggleSubQuestions = subCount > 0;
              return (
                <button
                  key={particle.id}
                  type="button"
                  onClick={() => {
                    if (!canToggleSubQuestions) return;
                    onToggleSubQuestionsExpanded?.(particle.id, !expanded);
                  }}
                  disabled={busy || saving || !canToggleSubQuestions}
                  className={[
                    'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold disabled:opacity-50',
                    canToggleSubQuestions && expanded
                      ? 'border-slate-400 bg-slate-50 text-blue-700'
                      : 'border-slate-200 bg-slate-100 text-slate-500',
                  ].join(' ')}
                >
                  {canToggleSubQuestions ? (
                    expanded
                      ? <Eye className="h-3.5 w-3.5" />
                      : <EyeOff className="h-3.5 w-3.5" />
                  ) : null}
                  {buildParticlePrintChipLabel(particle, index)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="relative min-h-0 flex-1 overflow-y-auto bg-[#eef2f6]">
        {!printSessionId || !hasPrintableParticles ? (
          <div className="flex h-full items-center justify-center px-8 text-center text-sm font-semibold text-slate-500">
            {particles.length
              ? '当前没有选中的知识颗粒，请在上方标签中重新开启参与打印。'
              : '暂无可预览内容'}
          </div>
        ) : (
          <div className="flex justify-center px-4 py-4">
            <RevisionPrintContinuousPreview
              particles={printableParticles}
              sessionId={sessionId}
              printSessionId={printSessionId}
              sheetKind={sheetKind}
              ttqaId={ttqaId}
              viewportWidth={viewportWidth}
            />
          </div>
        )}
      </div>
    </div>
  );
}
